import { FormattedMessage } from 'src/common/razzle-platform-translate'
import isArray from 'lodash/isArray'
import React from 'react'
import * as yup from 'yup'
import { Message, Validate } from 'react-hook-form'

type CustomMessage = Message | JSX.Element
type CustomValidateResult = CustomMessage | CustomMessage[] | boolean | undefined
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CustomValidate = Validate | ((data: any) => CustomValidateResult)

export type EmailValidator = (errorMsg?: JSX.Element) => CustomValidate

/**
 * @description Validates the value as an email address
 * @param {string} errorMsg
 */
export const email: EmailValidator = (errorMsg?: JSX.Element) => (value: string): JSX.Element | undefined => {
    if (!yup.string().email().isValidSync(value)) {
        return errorMsg ? (
            errorMsg
        ) : (
            <FormattedMessage id="L'email indiqué est invalide." defaultMessage="L'email indiqué est invalide." />
        )
    }
    return undefined
}

export type RequiredValidator = (errorMsg?: JSX.Element) => CustomValidate

/**
 * @description All field values apart from undefined and null meet this requirement
 * @param {string} errorMsg
 */
export const required: RequiredValidator = (errorMsg?: JSX.Element) => (
    value: string | number,
): JSX.Element | undefined => {
    if (!yup.string().required().isValidSync(value)) {
        return errorMsg ? (
            errorMsg
        ) : (
            <FormattedMessage id="Ce champ est obligatoire." defaultMessage="Ce champ est obligatoire." />
        )
    }
    return undefined
}

export type MinValidator = (length: number, errorMsg?: JSX.Element) => CustomValidate
/**
 * @description Set a minimum length limit for the string value
 * @param {number} length
 * @param {string} errorMsg
 */
export const min: MinValidator = (length: number, errorMsg?: string | JSX.Element) => (
    value: string,
): JSX.Element | undefined | string => {
    if (!yup.string().min(length).isValidSync(value)) {
        return errorMsg ? (
            errorMsg
        ) : (
            <FormattedMessage
                id="Caractères minimum c'est {min}"
                defaultMessage="Caractères minimum c'est {min}"
                values={{ min: length }}
            />
        )
    }
    return undefined
}

export type MaxValidator = (length: number, errorMsg?: JSX.Element) => CustomValidate

/**
 * @description Set a maximum length limit for the string value
 * @param {number} length
 * @param {string} errorMsg
 */
export const max: MaxValidator = (length: number, errorMsg?: JSX.Element) => (
    value: string,
): JSX.Element | undefined => {
    if (!yup.string().max(length).isValidSync(value)) {
        return errorMsg ? (
            errorMsg
        ) : (
            <FormattedMessage
                id="Caractères maximum c'est {max}"
                defaultMessage="Caractères maximum c'est {max}"
                values={{ min: length }}
            />
        )
    }
    return undefined
}

export type AcceptValidator = (errorMsg?: JSX.Element) => CustomValidate

/**
 * @description Validates the value as a boolean
 * @param {string} errorMsg
 */
export const accept: AcceptValidator = (errorMsg?: JSX.Element) => (value: boolean): JSX.Element | undefined => {
    if (!yup.boolean().isValidSync(value) || value === false) {
        return errorMsg ? (
            errorMsg
        ) : (
            <FormattedMessage id="Il faut accepter les condition" defaultMessage="Il faut accepter les condition" />
        )
    }
    return undefined
}

export type IsPositiveValidator = (errorMsg?: JSX.Element) => CustomValidate

/**
 * @description Validates the value to be a positive number
 * @param {string} errorMsg
 */
export const isPositive: IsPositiveValidator = (errorMsg?: JSX.Element) => (value: number): JSX.Element | undefined => {
    if (!yup.number().positive().isValidSync(value)) {
        return errorMsg ? (
            errorMsg
        ) : (
            <FormattedMessage
                id="La valeur doit être supérieur à 0"
                defaultMessage="La valeur doit être supérieur à 0"
            />
        )
    }
    return undefined
}

export type EqualValidator = (formValue: string | number, errorMsg?: JSX.Element) => CustomValidate

/**
 * @description Validator that insures that the provided value formValue
 * is equal to the value of the field
 * @param {string} formValue
 * @param {string} errorMsg
 */
export const equal: EqualValidator = (formValue: string | number, errorMsg?: JSX.Element) => (
    value: string | number,
): JSX.Element | undefined => {
    const isValid = yup
        .string()
        .test('equal', (v) => v === formValue)
        .isValidSync(value)

    if (!isValid) {
        return errorMsg ? (
            errorMsg
        ) : (
            <FormattedMessage id="Les valeurs ne sont pas égales" defaultMessage="Les valeurs ne sont pas égales" />
        )
    }
    return undefined
}

export type UrlValidator = (errorMsg?: JSX.Element) => CustomValidate

/**
 * @description Validates that the value is a url
 * @param {string} errorMsg
 */
export const url: UrlValidator = (errorMsg?: JSX.Element) => (value: string): JSX.Element | undefined => {
    if (!yup.string().url().isValidSync(value)) {
        return errorMsg ? (
            errorMsg
        ) : (
            <FormattedMessage id="L'url indiqué est invalide" defaultMessage="L'url indiqué est invalide" />
        )
    }
    return undefined
}

type Validation =
    | EmailValidator
    | RequiredValidator
    | MinValidator
    | MaxValidator
    | AcceptValidator
    | IsPositiveValidator
    | EqualValidator
    | UrlValidator

export type FieldValidate = Array<ReturnType<Validation>>

// We use this function to use multiple validator
// for example validators([required(), email()])
// we are returned the first error obtained
export const validators = (items: FieldValidate) => (value: any): JSX.Element | void => {
    if (!isArray(items)) {
        throw Error('validators params must be array')
    }

    for (const item of items) {
        const error: any = item(value)
        if (error) {
            return error
        }
    }
}
